<template>
  <div ref="rootRef" class="relative w-full" @keydown="onKeydown">

    <button
      :id="`${uid}-trigger`"
      type="button"
      role="combobox"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      :aria-controls="`${uid}-list`"
      :aria-activedescendant="isOpen && activeIndex >= 0 ? `${uid}-opt-${activeIndex}` : undefined"
      :disabled="disabled"
      class="flex h-11 w-full items-center justify-between gap-2 rounded-[var(--radius-md)] border bg-card px-3.5 text-start text-[15px] outline-none transition-all duration-200 focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
      :class="[ringClass, isOpen ? 'ring-2' : 'hover:border-primary/50']"
      @click="toggle"
    >
      <span
        class="truncate"
        :class="selectedOption ? 'text-foreground' : 'text-muted-foreground'"
      >
        {{ selectedOption ? selectedOption.label : placeholder }}
      </span>

      <svg
        class="size-4 shrink-0 text-muted-foreground transition-transform duration-200"
        :class="isOpen && 'rotate-180'"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="m6 9 6 6 6-6" />
      </svg>
    </button>

    <Transition
      enter-active-class="transition-opacity duration-200"
      leave-active-class="transition-opacity duration-150"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOpen"
        class="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px] md:hidden"
        aria-hidden="true"
        @click="close()"
      />
    </Transition>

    <Transition
      enter-active-class="transition duration-200 ease-out"
      leave-active-class="transition duration-150 ease-in"
      enter-from-class="translate-y-full opacity-0 md:translate-y-0 md:-translate-y-1 md:scale-[0.98]"
      leave-to-class="translate-y-full opacity-0 md:translate-y-0 md:-translate-y-1 md:scale-[0.98]"
    >
      <div
        v-if="isOpen"
        class="fixed inset-x-0 bottom-0 z-50 flex max-h-[75dvh] flex-col overflow-hidden rounded-t-2xl border border-border bg-card pb-[env(safe-area-inset-bottom)] shadow-2xl md:absolute md:inset-x-auto md:bottom-auto md:start-0 md:top-full md:mt-1.5 md:w-full md:max-h-72 md:rounded-[var(--radius-md)] md:pb-0 md:shadow-xl"
        @click.prevent
      >
        <div class="mx-auto mt-2.5 h-1 w-10 shrink-0 rounded-full bg-border md:hidden" />

        <div v-if="showSearch" class="shrink-0 p-2">
          <input
            ref="searchRef"
            v-model="query"
            type="text"
            autocomplete="off"
            placeholder="جستجو..."
            class="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground outline-none transition-shadow placeholder:text-muted-foreground focus:ring-2 focus:ring-primary"
            @input="activeIndex = filteredOptions.length ? 0 : -1"
          />
        </div>

        <ul
          :id="`${uid}-list`"
          ref="listRef"
          role="listbox"
          class="flex-1 overflow-y-auto overscroll-contain p-1.5"
        >
          <li
            v-for="(opt, i) in filteredOptions"
            :id="`${uid}-opt-${i}`"
            :key="opt.value"
            role="option"
            :aria-selected="opt.value === inputValue"
            class="flex cursor-pointer items-center justify-between gap-2 rounded-lg px-3 py-3 text-[15px] transition-colors md:py-2.5"
            :class="[
              i === activeIndex ? 'bg-primary/10' : '',
              opt.value === inputValue ? 'font-medium text-primary' : 'text-foreground',
            ]"
            @mousemove="activeIndex = i"
            @click="select(opt.value)"
          >
            <span class="truncate">{{ opt.label }}</span>

            <svg
              v-if="opt.value === inputValue"
              class="size-4 shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </li>

          <li
            v-if="!filteredOptions.length"
            class="px-3 py-6 text-center text-sm text-muted-foreground"
          >
            موردی پیدا نشد
          </li>
        </ul>
      </div>
    </Transition>

    <ErrorMessage v-if="name" :name="name" />

  </div>
</template>


<script setup lang="ts">
import { useField } from 'vee-validate'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useId, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    name?: string
    modelValue?: string | null
    options: { value: string; label: string }[]
    invalid?: boolean
    valid?: boolean
    placeholder?: string
    disabled?: boolean
    searchable?: boolean | 'auto'
  }>(),
  {
    placeholder: 'انتخاب کنید',
    searchable: 'auto',
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const { value: fieldValue, handleBlur } = useField<string>(() => props.name || '', undefined, {
  initialValue: props.modelValue || '',
  syncVModel: true,
})

const inputValue = computed({
  get: () => (props.name ? fieldValue.value : props.modelValue || ''),

  set: (value: string) => {
    if (props.name) {
      fieldValue.value = value
    }

    emit('update:modelValue', value)
  },
})

const ringClass = computed(() => {
  if (props.invalid) return 'border-[var(--danger)] focus-visible:ring-[var(--danger)] ring-[var(--danger)]'

  if (props.valid) return 'border-[var(--success)] focus-visible:ring-[var(--success)] ring-[var(--success)]'

  return 'border-border focus-visible:ring-primary ring-primary'
})

const uid = useId()
const rootRef = ref<HTMLElement | null>(null)
const listRef = ref<HTMLElement | null>(null)
const searchRef = ref<HTMLInputElement | null>(null)

const isOpen = ref(false)
const query = ref('')
const activeIndex = ref(-1)

const selectedOption = computed(() => props.options.find((o) => o.value === inputValue.value))

const showSearch = computed(() => (props.searchable === 'auto' ? props.options.length > 8 : props.searchable))

const normalize = (s: string) => s.replace(/ي/g, 'ی').replace(/ك/g, 'ک').trim().toLowerCase()

const filteredOptions = computed(() => {
  const q = normalize(query.value)

  if (!q) return props.options

  return props.options.filter((o) => normalize(o.label).includes(q))
})

const isMobile = () => window.matchMedia('(max-width: 767px)').matches

function open() {
  if (props.disabled || isOpen.value) return

  query.value = ''
  activeIndex.value = Math.max(
    0,
    props.options.findIndex((o) => o.value === inputValue.value)
  )
  isOpen.value = true

  nextTick(() => {
    scrollToActive()

    if (showSearch.value && !isMobile()) searchRef.value?.focus()
  })
}

function close(refocus = true) {
  if (!isOpen.value) return

  isOpen.value = false

  if (props.name) handleBlur()

  if (refocus) document.getElementById(`${uid}-trigger`)?.focus()
}

function toggle() {
  isOpen.value ? close() : open()
}

function select(value: string) {
  inputValue.value = value
  close()
}

function scrollToActive() {
  document.getElementById(`${uid}-opt-${activeIndex.value}`)?.scrollIntoView({ block: 'nearest' })
}

function move(step: number) {
  const len = filteredOptions.value.length

  if (!len) return

  activeIndex.value = (activeIndex.value + step + len) % len

  nextTick(scrollToActive)
}

function onKeydown(e: KeyboardEvent) {
  if (props.disabled) return

  if (!isOpen.value) {
    if (['ArrowDown', 'ArrowUp'].includes(e.key)) {
      e.preventDefault()
      open()
    }

    return
  }

  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault()
      move(1)
      break
    case 'ArrowUp':
      e.preventDefault()
      move(-1)
      break
    case 'Home':
      if (!showSearch.value) {
        e.preventDefault()
        activeIndex.value = 0
        scrollToActive()
      }
      break
    case 'End':
      if (!showSearch.value) {
        e.preventDefault()
        activeIndex.value = filteredOptions.value.length - 1
        scrollToActive()
      }
      break
    case 'Enter': {
      e.preventDefault()

      const opt = filteredOptions.value[activeIndex.value]

      if (opt) select(opt.value)
      break
    }
    case 'Escape':
      e.preventDefault()
      close()
      break
    case 'Tab':
      close(false)
      break
  }
}

function onPointerDown(e: PointerEvent) {
  if (isOpen.value && rootRef.value && !rootRef.value.contains(e.target as Node)) close(false)
}

onMounted(() => document.addEventListener('pointerdown', onPointerDown))

watch(isOpen, (val) => {
  if (!import.meta.client) return

  document.body.style.overflow = val && isMobile() ? 'hidden' : ''
})

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onPointerDown)

  document.body.style.overflow = ''
})
</script>
